import React, { useEffect, useRef } from 'react';
import { Button, ButtonGroup, Modal, Text } from '@fragments';
import { IconAlertCircle, IconCheck, IconFingerprint, IconLayoutGrid, IconMovie, IconRefresh, IconX } from '@tabler/icons-react';
import usePersonalityPicker from './hooks/usePersonalityPicker';
import usePersonalityView from './hooks/usePersonalityView';
import ReelGrid from './components/ReelGrid';
import ReelTray from './components/ReelTray';
import MappingProgress from './components/MappingProgress';
import PersonalityView from './components/PersonalityView';
import { personalityBounds, unavailable } from './format';
import './styles.scss';

const slot = (id, cover, label, failed = null) => ({ id, cover: cover || null, label: label || '', failed });

// What a failed mapping did with each of its Reels, in their places: ready (kept, its
// transcript reused) or why it could not be used. Why it failed, that nothing was charged,
// and whether to swap the marked Reels or just try again.
function FailureNotice({ t, failure, retry, hasPersonality, onDismiss }) {
  const swap = retry?.swap || 0;
  const title = swap ? t('personalityFailedTitle', { count: swap }) : t('personalityFailedRetryTitle');
  const text = `${swap ? t('personalityFailedSwap', { count: swap }) : t('personalityFailedRetry')} ${t(hasPersonality ? 'personalityFailedKeep' : 'personalityFailedFree')}`;
  return <div className="studio-personality__notice" role="alert">
    <div className="studio-personality__notice-head">
      <IconAlertCircle aria-hidden="true" />
      <div className="studio-personality__notice-text">
        <Text.label contrast={9}>{title}</Text.label>
        <Text.caption contrast={7}>{text}</Text.caption>
      </div>
      <Button variant="text" color="overlay" size="small" preventLoading icon={<IconX />} aria-label={t('close')} onClick={onDismiss} />
    </div>
    <ol className="studio-personality__outcome">
      {failure.sources.map((source, index) => {
        const failed = source.status === 'failed';
        return <li key={source.video_id} className={failed ? 'is-failed' : 'is-ready'}>
          <span className="studio-personality__outcome-thumb">{source.preview?.url ? <img src={source.preview.url} alt="" /> : <IconMovie aria-hidden="true" />}</span>
          <span className="studio-personality__outcome-text">
            <Text.caption contrast={6}>{t('personalityPosition', { position: index + 1 })}</Text.caption>
            <Text.caption contrast={failed ? 9 : 8} left={failed ? <IconAlertCircle /> : <IconCheck />}>
              {failed ? t(`personalitySourceFailed_${source.error_code}`, { defaultValue: t('personalitySourceFailed') }) : t('personalitySourceReady')}
            </Text.caption>
          </span>
        </li>;
      })}
    </ol>
  </div>;
}

// "Personalidade" (ClickUp 868mhqewg, contract 2.3.0; My Voice before): the active
// profile's Reels to choose 3 from, where the creator talks to the camera. The AI learns
// their personality, and that profile's scripts keep it on the structure that goes viral.
// Opened from the composer's personality button, and once right after an onboarding
// (`origin: onboarding`, with "Pular", which stores nothing). The footer holds the choice
// minimized (or, before one, the Reels of the personality in use), its quoted ceiling and the
// start. After a failed mapping the choice starts from it: the Reels that worked stay, the
// ones that failed are marked to swap, and only those need a new pick. A running mapping
// shows its progress; a mapped personality can be read in "Sua personalidade". Out of
// credits opens the plans (PlanDialog), as everywhere else.
export default function PersonalityDialog({ studio: s, origin = 'composer' }) {
  const { t, myPersonality, profile } = s;
  const picker = usePersonalityPicker(s);
  const pane = usePersonalityView(myPersonality);
  // Each pane opens at its top: the Reels' scroll would otherwise carry over into the progress,
  // where a heading that grows (`comparing` wraps on phones) is then anchored out of view.
  const content = useRef(null);
  useEffect(() => {
    const scroller = content.current?.closest('.fragment-modal__body');
    if (scroller) scroller.scrollTop = 0;
  }, [pane.view]);
  const bounds = personalityBounds(s.capabilities);
  const { personality, mapping, retry } = myPersonality;
  const handle = profile?.handle || '';
  const close = () => s.setModal(null);
  const day = iso => new Intl.DateTimeFormat(s.locale.replace('_', '-'), { day: 'numeric', month: 'short' }).format(new Date(iso));
  const current = (personality?.sources || []).map(source => slot(source.video_id, source.preview?.url, source.caption));
  const reason = code => t(unavailable(code).label, bounds);

  let footer;
  if (pane.view === 'progress') {
    // No footer action while the Reels are analyzed: the header's close is enough.
    footer = <ReelTray t={t} slots={picker.needed} items={(mapping?.sources || []).map(source => slot(source.video_id, source.preview?.url, source.caption))}
      title={t('personalityTrayMapping')} detail={<Text.caption contrast={6}>{t('personalityCloseHint')}</Text.caption>} />;
  } else if (pane.view === 'personality' && personality) {
    footer = <ReelTray t={t} slots={picker.needed} items={current} title={t('personalityTrayCurrent')}
      detail={<Text.caption contrast={6}>{t('personalityMappedOn', { date: day(personality.timestamps.mapped) })}</Text.caption>}>
      <Button variant="rounded" color="overlay" preventLoading startIcon={<IconRefresh />} onClick={() => pane.setView('reels')}>{t('personalityUpdate')}</Button>
      <Button variant="rounded" color="inverse" preventLoading onClick={close}>{t('personalityDone')}</Button>
    </ReelTray>;
  } else {
    const { selected, swaps, needed, quote } = picker;
    const picking = selected.length > 0;
    const missing = needed - selected.length;
    const swapAt = swaps.length ? selected.indexOf(swaps[0]) + 1 : 0;
    const swapDetail = swaps.length === 1 ? `${reason(swaps[0].failed)}. ${t('personalityTraySwapHint')}`
      : `${swaps.map(item => `${t('personalityPosition', { position: selected.indexOf(item) + 1 })}: ${reason(item.failed)}`).join(' · ')}. ${t('personalityTraySwapHint')}`;
    const detail = picker.error ? <Text.caption contrast={8} role="alert">{t(`error_${picker.error}`, { defaultValue: t('error_generic') })}</Text.caption>
      : swaps.length ? <Text.caption contrast={7}>{swapDetail}</Text.caption>
      : !picking ? <Text.caption contrast={6}>{personality ? t('personalityMappedOn', { date: day(personality.timestamps.mapped) }) : t('personalityTrayEmptyHint', bounds)}</Text.caption>
      : missing > 0 ? <Text.caption contrast={6}>{t('personalityTrayMissing', { count: missing })}</Text.caption>
      : quote.status === 'loading' ? <Text.caption contrast={6} loading>{t('personalityQuoting')}</Text.caption>
      : quote.status === 'error' ? <Text.caption contrast={8} role="alert">{t(`error_${quote.error}`, { defaultValue: t('error_generic') })}</Text.caption>
      : quote.value ? <Text.caption contrast={7}>{t('personalityQuote', { credits: quote.value.quoted_units })}</Text.caption> : null;
    const title = swaps.length ? t('personalityTraySwap', { count: swaps.length, position: swapAt })
      : picking ? t('personalityTraySelected', { count: selected.length, total: needed })
      : personality ? t('personalityTrayCurrent') : t('personalityTrayEmpty', { count: needed });
    footer = <ReelTray t={t} slots={needed} items={picking ? selected.map(video => slot(video.id, video.preview?.url, video.caption, video.failed ? reason(video.failed) : null)) : current}
      removable={picking} onRemove={picker.remove} detail={detail} title={title}>
      {origin === 'onboarding' && !personality && <Button variant="text" color="overlay" preventLoading onClick={close}>{t('personalitySkip')}</Button>}
      <Button variant="rounded" color="inverse" disabled={quote.status !== 'ready' || picker.starting} onClick={picker.confirm}>
        {t(retry ? 'personalityRetry' : personality ? 'personalityUpdate' : 'personalityMap')}
      </Button>
    </ReelTray>;
  }

  const failure = pane.view === 'reels' ? pane.failure : null;
  return <Modal open size={920} closeLabel={t('close')} onClose={close} className="studio-dialog studio-personality-dialog" footer={footer}
    eyebrow={origin === 'onboarding' ? t('personalityEyebrowOnboarding') : undefined} title={t('personality')}
    subtitle={t(personality ? 'personalitySubtitleReady' : 'personalitySubtitle', { handle, count: picker.needed })}>
    <div ref={content} className="studio-personality">
      {failure && <FailureNotice t={t} failure={failure} retry={retry} hasPersonality={!!personality} onDismiss={pane.dismiss} />}
      {pane.view !== 'progress' && personality && <div className="studio-personality__bar">
        <ButtonGroup size="small" value={pane.view} onChange={pane.setView}
          options={[{ label: t('personalityTabReels'), value: 'reels', icon: <IconLayoutGrid /> }, { label: t('personalityTabProfile'), value: 'personality', icon: <IconFingerprint /> }]} />
      </div>}
      {pane.view === 'progress' && mapping ? <MappingProgress studio={s} mapping={mapping} hasPersonality={!!personality} />
        : pane.view === 'personality' && personality ? <PersonalityView studio={s} personality={personality} mapped={pane.mapped} />
          : <ReelGrid studio={s} picker={picker} bounds={bounds} />}
    </div>
  </Modal>;
}
